/**
 * Sensor / live-overlay model types — WP-8.
 *
 * Pure-data interfaces consumed by `SensorOverlayToolCard.tsx`. No
 * runtime dependencies and no token / DOM imports so the model can
 * be unit-tested in isolation.
 *
 * Contract source:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 *     §7 Card 8 (Sensor / live overlay tools) and §8.8 (WP-8).
 *   - `geosoft-platform-design-system/docs/INTEGRATIONS_INVENTORY.md`
 *     "Integration UI Pattern" — connector setup, masked credentials,
 *     last-sync timestamp.
 *   - `geosoft-platform-design-system/docs/PRODUCT_LIFECYCLE_SURFACE_EXPANSION.md`
 *     §"Simulation and Animation Workspaces" + §"Warning, Alert, And
 *     Incident System" (live vs simulated, alert severity model).
 */

import type { SensorPlayMode, SensorPlayStatus } from '@/state/useViewerStore';

/** Stale freshness threshold (ms). Past this, the card surfaces a
 *  warning banner. Aligned with WP-8 spec ("Stale > 30s"). */
export const SENSOR_STALE_THRESHOLD_MS = 30 * 1000;

/**
 * Connector permission state surfaced to the card. Maps 1:1 to
 * `IntegrationOperationalState.permissionState` semantics from the
 * scene index, with an explicit `granted` value when access is OK.
 */
export type SensorPermissionState = 'granted' | 'permission-denied';

/** Connectivity state surfaced to the card. */
export type SensorConnectivityState = 'online' | 'offline';

/** Severity of an alert raised by a sensor. Mirrors the
 *  `Banner` severity scale in the design bridge primitives. */
export type SensorAlertSeverity = 'info' | 'warning' | 'critical';

/**
 * A single alert active on the linked sensor / asset. Surfaced as a
 * red badge + count at the top of the card (per spec).
 */
export interface SensorAlert {
  readonly id: string;
  readonly label: string;
  readonly severity: SensorAlertSeverity;
  readonly raisedAtIso?: string;
}

/** A linked asset / layer the card can pivot to. Clicking the chip
 *  dispatches a `viewer:tool-card-request` event so the right-rail
 *  ToolCardDock activates the correct family card. */
export interface SensorLinkedTarget {
  /** Visible label, e.g. "Pump 101" or "BIM model". */
  readonly label: string;
  /** Renderable layer id when the target is a layer. */
  readonly layerId?: string;
  /** Layer kind (`bim`, `vector`, ...) so the dispatched event can
   *  resolve the right ToolCard family. */
  readonly layerKind?: string;
  /** Scene-index node id when the target is an index node. */
  readonly indexNodeId?: string;
  /** Optional inner feature id (e.g. IFC GUID). */
  readonly featureId?: string | number;
}

/** Source connector / integration metadata. */
export interface SensorConnectorInfo {
  /** Human-readable provider / connector name (e.g. "Mapbox",
   *  "OPC UA — Plant 1"). */
  readonly name: string;
  /** Provider type from the integrations inventory taxonomy
   *  (`opcua`, `mqtt`, `https-poll`, `webhook`, ...). */
  readonly providerType?: string;
  /**
   * Endpoint host, displayed in MONO. Path / scheme are stripped by
   * {@link maskEndpoint} so secrets never leak into the UI.
   */
  readonly endpointHost?: string;
  /**
   * Optional last-4 of the credential identifier. Renders behind a
   * mask glyph; display only — never logged.
   */
  readonly credentialLast4?: string;
  /** Optional rate-limit hint, e.g. `"60 req/min"`. */
  readonly rateLimit?: string;
  /** Optional scope chip, e.g. `"read:telemetry"`. */
  readonly scope?: string;
}

/**
 * Full state passed to the SensorOverlayToolCard. The card is a pure
 * presentation component over this model; tests drive the matrix by
 * passing different shapes here.
 */
export interface SensorOverlayState {
  /** Display title; defaults to "Live overlay" when omitted. */
  readonly title?: string;
  /** Underlying layer / index node id, surfaced as a MONO subtitle. */
  readonly sourceId?: string;
  /** Last-update timestamp (ISO-8601). Paired with the relative-time
   *  label in MONO, per spec. */
  readonly lastUpdateIso?: string;

  /**
   * Optional mode override. When omitted the
   * {@link SensorOverlayToolCard} reads the canonical value from
   * `useViewerStore.sensorPlayMode`.
   */
  readonly mode?: SensorPlayMode;
  /**
   * Optional status override. When omitted the
   * {@link SensorOverlayToolCard} reads the canonical value from
   * `useViewerStore.sensorPlayStatus`.
   */
  readonly status?: SensorPlayStatus;

  readonly permission: SensorPermissionState;
  readonly connectivity: SensorConnectivityState;

  readonly linkedTargets?: readonly SensorLinkedTarget[];
  readonly alerts?: readonly SensorAlert[];
  readonly connector?: SensorConnectorInfo;

  /** When true, render the loading skeleton variant. */
  readonly loading?: boolean;
  /** When set, render the error variant inside the card. */
  readonly errorMessage?: string;
}

/** Compute milliseconds since `lastUpdateIso`, or `null` when missing
 *  or unparseable. `nowMs` is injectable for deterministic tests. */
export function computeFreshnessMs(
  lastUpdateIso: string | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!lastUpdateIso) return null;
  const t = Date.parse(lastUpdateIso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, nowMs - t);
}

/** True when the overlay's last update is older than the stale
 *  threshold. Used to flip the freshness Banner on. */
export function isStale(
  lastUpdateIso: string | undefined,
  nowMs: number = Date.now(),
  thresholdMs: number = SENSOR_STALE_THRESHOLD_MS,
): boolean {
  const ms = computeFreshnessMs(lastUpdateIso, nowMs);
  return ms !== null && ms > thresholdMs;
}

/**
 * Render a freshness label such as `"12 s ago"`. Compact, MONO-safe,
 * deterministic — no locale-dependent words past `s` / `min` / `h`.
 */
export function formatFreshness(
  lastUpdateIso: string | undefined,
  nowMs: number = Date.now(),
): string {
  const ms = computeFreshnessMs(lastUpdateIso, nowMs);
  if (ms === null) return 'never';
  if (ms < 1000) return 'just now';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} d ago`;
}

/**
 * Mask an endpoint string so only the host is visible, e.g.
 * `https://mqtt.example.com:8883/topic` → `mqtt.example.com`.
 *
 * Per `INTEGRATIONS_INVENTORY.md` "credential handling" rule we
 * never display query strings or path segments in chrome.
 */
export function maskEndpoint(endpoint: string | undefined): string {
  if (!endpoint) return '';
  try {
    const url = new URL(endpoint);
    return url.host || endpoint;
  } catch {
    return endpoint.replace(/^[a-z][a-z0-9+\-.]*:\/\//i, '').split('/')[0] ?? endpoint;
  }
}

/** Mask a credential identifier so only the last 4 characters are
 *  visible, prefixed by a fixed-width run of bullets. Empty / short
 *  values render as a single bullet so layout stays stable. */
export function maskCredentialLast4(last4: string | undefined): string {
  if (!last4) return '••••';
  const tail = last4.slice(-4);
  return `••••${tail}`;
}

/** Highest severity present in the alert list, or `null`. */
export function highestAlertSeverity(
  alerts: readonly SensorAlert[] | undefined,
): SensorAlertSeverity | null {
  if (!alerts || alerts.length === 0) return null;
  const order: Record<SensorAlertSeverity, number> = {
    critical: 3,
    warning: 2,
    info: 1,
  };
  return alerts.reduce<SensorAlertSeverity>((acc, a) => {
    return order[a.severity] > order[acc] ? a.severity : acc;
  }, alerts[0].severity);
}
